import { useParams, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  MapPin, Phone, Mail, Clock, Star, ChevronRight,
  Scissors, Calendar, Instagram, MessageCircle, ArrowLeft, ShoppingBag,
  Facebook
} from "lucide-react";

export default function SalonPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const { data: salon, isLoading: loadingSalon } = trpc.salon.getBySlug.useQuery(
    { slug: slug ?? "" },
    { enabled: !!slug }
  );

  // Stable query inputs
  const salonId = useMemo(() => salon?.id ?? 0, [salon?.id]);
  const salonServicesInput = useMemo(() => ({ salonId }), [salonId]);
  const salonProfsInput = useMemo(() => ({ salonId }), [salonId]);
  const salonReviewsInput = useMemo(() => ({ salonId }), [salonId]);
  const salonStatsInput = useMemo(() => ({ salonId }), [salonId]);

  const { data: services } = trpc.services.list.useQuery(
    salonServicesInput,
    { enabled: !!salon?.id }
  );

  const { data: professionals } = trpc.professionals.list.useQuery(
    salonProfsInput,
    { enabled: !!salon?.id }
  );

  const { data: reviews } = trpc.reviews.listPublic.useQuery(
    salonReviewsInput,
    { enabled: !!salon?.id }
  );

  const { data: reviewStats } = trpc.reviews.getStats.useQuery(
    salonStatsInput,
    { enabled: !!salon?.id }
  );

  if (loadingSalon) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!salon) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Scissors className="w-16 h-16 text-muted-foreground mx-auto" />
          <h1 className="text-2xl font-bold">Salão não encontrado</h1>
          <p className="text-muted-foreground">O link que você acessou não existe ou foi removido.</p>
          <Button onClick={() => navigate("/")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao início
          </Button>
        </div>
      </div>
    );
  }

  // ── Customizações dinâmicas ──────────────────────────────────────────────────
  const s = salon as any;
  const themeColor    = s.themeColor    || "#C97D2E";
  const themeBgColor  = s.themeBgColor  || "#F5F0E8";
  const themeDarkColor = s.themeDarkColor || "#2C1810";
  const heroSlogan    = s.heroSlogan    || salon.name;
  const heroSubtitle  = s.heroSubtitle  || salon.description || "";
  const ctaButtonText = s.ctaButtonText || "Agendar agora";
  const bannerUrl     = s.bannerUrl     || null;
  const instagramUrl  = s.instagramUrl  || null;
  const facebookUrl   = s.facebookUrl   || null;
  const tiktokUrl     = s.tiktokUrl     || null;
  const whatsappUrl   = s.whatsappUrl   || (salon.phone ? `https://wa.me/${salon.phone.replace(/\D/g, "")}` : null);
  const showTeamSection    = s.showTeamSection    !== false;
  const showReviewsSection = s.showReviewsSection !== false;
  const showHoursSection   = s.showHoursSection   !== false;
  // ────────────────────────────────────────────────────────────────────────────

  // Categorias únicas de serviços
  const categories = services
    ? ["all", ...Array.from(new Set(services.map((sv: any) => sv.category).filter(Boolean)))]
    : ["all"];

  const filteredServices = services?.filter((sv: any) =>
    selectedCategory === "all" || sv.category === selectedCategory
  ) ?? [];

  // Horários de funcionamento formatados
  const parseOpeningHours = (raw: string | null | undefined) => {
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };
  const openingHours = parseOpeningHours(salon.openingHours);
  const dayNames: Record<string, string> = {
    monday: "Segunda", tuesday: "Terça", wednesday: "Quarta",
    thursday: "Quinta", friday: "Sexta", saturday: "Sábado", sunday: "Domingo"
  };

  const avgRating = Number(reviewStats?.averageRating ?? 0);
  const totalReviews = Number(reviewStats?.totalReviews ?? 0);

  return (
    <div className="min-h-screen" style={{ backgroundColor: themeBgColor, color: themeDarkColor }}>
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur border-b" style={{ backgroundColor: `${themeBgColor}f0`, borderColor: `${themeColor}33` }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm transition-opacity hover:opacity-70"
            style={{ color: themeDarkColor }}
          >
            <ArrowLeft className="w-4 h-4" />
            Beleza &amp; Bronze
          </button>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              style={{ borderColor: `${themeColor}66`, color: themeColor }}
              onClick={() => navigate(`/s/${slug}/loja`)}
            >
              <ShoppingBag className="w-4 h-4" />
              <span className="hidden sm:inline">Loja</span>
            </Button>
            <Button
              size="sm"
              style={{ backgroundColor: themeColor, color: "#fff" }}
              onClick={() => navigate(`/booking?salon=${salon.id}`)}
            >
              <Calendar className="w-4 h-4 mr-2" />
              {ctaButtonText}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative text-white overflow-hidden"
        style={{ minHeight: bannerUrl ? "420px" : "320px" }}
      >
        {/* Fundo: banner ou gradiente */}
        {bannerUrl ? (
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${bannerUrl})` }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${themeDarkColor} 0%, ${themeColor}99 100%)` }}
          />
        )}
        {/* Overlay escuro para legibilidade */}
        <div className="absolute inset-0 bg-black/50" />

        <div className="relative max-w-5xl mx-auto px-4 py-16 flex flex-col md:flex-row gap-8 items-center">
          {/* Logo / Avatar */}
          <div className="w-24 h-24 md:w-32 md:h-32 rounded-2xl border-2 flex items-center justify-center flex-shrink-0 overflow-hidden"
            style={{ borderColor: `${themeColor}88`, backgroundColor: `${themeColor}22` }}>
            {salon.logoUrl ? (
              <img src={salon.logoUrl} alt={salon.name} className="w-full h-full object-cover" />
            ) : (
              <Scissors className="w-12 h-12" style={{ color: themeColor }} />
            )}
          </div>

          {/* Info principal */}
          <div className="flex-1 space-y-3 text-center md:text-left">
            <div>
              <h1 className="text-3xl md:text-5xl font-bold leading-tight">{heroSlogan}</h1>
              {heroSubtitle && (
                <p className="text-white/80 mt-2 text-lg leading-relaxed">{heroSubtitle}</p>
              )}
            </div>

            {/* Rating */}
            {totalReviews > 0 && (
              <div className="flex items-center gap-2 justify-center md:justify-start">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map(star => (
                    <Star
                      key={star}
                      className={`w-5 h-5 ${star <= Math.round(avgRating) ? "fill-current" : "opacity-30"}`}
                      style={{ color: themeColor }}
                    />
                  ))}
                </div>
                <span className="font-semibold" style={{ color: themeColor }}>{avgRating.toFixed(1)}</span>
                <span className="text-white/60">({totalReviews} avaliações)</span>
              </div>
            )}

            {/* Contato */}
            <div className="flex flex-wrap gap-4 text-white/70 text-sm justify-center md:justify-start">
              {salon.address && (
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-4 h-4" style={{ color: themeColor }} />
                  {salon.address}
                </span>
              )}
              {salon.phone && (
                <a href={`tel:${salon.phone}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                  <Phone className="w-4 h-4" style={{ color: themeColor }} />
                  {salon.phone}
                </a>
              )}
              {salon.email && (
                <a href={`mailto:${salon.email}`} className="flex items-center gap-1.5 hover:text-white transition-colors">
                  <Mail className="w-4 h-4" style={{ color: themeColor }} />
                  {salon.email}
                </a>
              )}
            </div>

            {/* Redes sociais */}
            {(instagramUrl || facebookUrl || tiktokUrl || whatsappUrl) && (
              <div className="flex gap-3 justify-center md:justify-start">
                {instagramUrl && (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                    style={{ backgroundColor: `${themeColor}33`, color: themeColor }}>
                    <Instagram className="w-4 h-4" />
                  </a>
                )}
                {facebookUrl && (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                    style={{ backgroundColor: `${themeColor}33`, color: themeColor }}>
                    <Facebook className="w-4 h-4" />
                  </a>
                )}
                {tiktokUrl && (
                  <a href={tiktokUrl} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80 text-xs font-bold"
                    style={{ backgroundColor: `${themeColor}33`, color: themeColor }}>
                    TK
                  </a>
                )}
                {whatsappUrl && (
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                    className="w-9 h-9 rounded-full flex items-center justify-center transition-opacity hover:opacity-80"
                    style={{ backgroundColor: `${themeColor}33`, color: themeColor }}>
                    <MessageCircle className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {/* CTA desktop */}
          <div className="hidden md:flex flex-col gap-2">
            <Button
              size="lg"
              className="text-white text-base px-8 py-6"
              style={{ backgroundColor: themeColor }}
              onClick={() => navigate(`/booking?salon=${salon.id}`)}
            >
              <Calendar className="w-5 h-5 mr-2" />
              {ctaButtonText}
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10 text-base px-8 py-6"
              onClick={() => navigate(`/s/${slug}/loja`)}
            >
              <ShoppingBag className="w-5 h-5 mr-2" />
              Ver produtos
            </Button>
            {whatsappUrl && (
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 mt-3 text-sm text-white/60 hover:text-white transition-colors"
              >
                <MessageCircle className="w-4 h-4" />
                Falar no WhatsApp
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Conteúdo principal */}
      <div className="max-w-5xl mx-auto px-4 py-10 space-y-12">

        {/* Serviços */}
        {filteredServices.length > 0 || (services && services.length > 0) ? (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ color: themeDarkColor }}>Serviços</h2>
              <span className="text-sm" style={{ color: `${themeDarkColor}88` }}>{services?.length ?? 0} serviços</span>
            </div>

            {/* Filtro por categoria */}
            {categories.length > 1 && (
              <div className="flex gap-2 flex-wrap mb-6">
                {categories.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className="px-4 py-1.5 rounded-full text-sm font-medium transition-colors border"
                    style={selectedCategory === cat
                      ? { backgroundColor: themeColor, color: "#fff", borderColor: themeColor }
                      : { backgroundColor: "transparent", color: `${themeDarkColor}99`, borderColor: `${themeDarkColor}33` }
                    }
                  >
                    {cat === "all" ? "Todos" : cat}
                  </button>
                ))}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredServices.map((service: any) => (
                <Card
                  key={service.id}
                  className="group cursor-pointer transition-all hover:shadow-md"
                  style={{ backgroundColor: "#fff", borderColor: `${themeColor}22` }}
                  onClick={() => navigate(`/booking?salon=${salon.id}&service=${service.id}`)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-base truncate" style={{ color: themeDarkColor }}>{service.name}</h3>
                          {service.category && (
                            <Badge variant="secondary" className="text-xs shrink-0">{service.category}</Badge>
                          )}
                        </div>
                        {service.description && (
                          <p className="text-sm line-clamp-2" style={{ color: `${themeDarkColor}88` }}>{service.description}</p>
                        )}
                        <div className="flex items-center gap-3 mt-3 text-sm" style={{ color: `${themeDarkColor}66` }}>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {service.durationMinutes} min
                          </span>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xl font-bold" style={{ color: themeColor }}>
                          R$ {Number(service.price).toFixed(2).replace(".", ",")}
                        </p>
                        <ChevronRight className="w-4 h-4 ml-auto mt-1 transition-colors" style={{ color: `${themeColor}88` }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        ) : null}

        {/* Profissionais */}
        {showTeamSection && professionals && professionals.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold mb-6" style={{ color: themeDarkColor }}>Nossa Equipe</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {professionals.map((pro: any) => (
                <Card key={pro.id} className="text-center transition-all hover:shadow-md"
                  style={{ backgroundColor: "#fff", borderColor: `${themeColor}22` }}>
                  <CardContent className="p-5">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3 overflow-hidden"
                      style={{ backgroundColor: `${themeColor}22` }}>
                      {pro.avatarUrl ? (
                        <img src={pro.avatarUrl} alt={pro.name} className="w-16 h-16 rounded-full object-cover" />
                      ) : (
                        <span className="text-2xl font-bold" style={{ color: themeColor }}>
                          {pro.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-sm" style={{ color: themeDarkColor }}>{pro.name}</h3>
                    {pro.specialties && (
                      <p className="text-xs mt-1 line-clamp-2" style={{ color: `${themeDarkColor}88` }}>{pro.specialties}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        {/* Horários */}
        {showHoursSection && openingHours && (
          <section>
            <h2 className="text-2xl font-bold mb-6" style={{ color: themeDarkColor }}>Horários de Funcionamento</h2>
            <Card style={{ backgroundColor: "#fff", borderColor: `${themeColor}22` }}>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {Object.entries(openingHours).map(([day, hours]: [string, any]) => (
                    <div key={day} className="flex items-center justify-between py-2 border-b last:border-0"
                      style={{ borderColor: `${themeColor}22` }}>
                      <span className="font-medium text-sm" style={{ color: themeDarkColor }}>{dayNames[day] ?? day}</span>
                      {hours?.isOpen ? (
                        <span className="text-sm" style={{ color: `${themeDarkColor}88` }}>
                          {hours.open} – {hours.close}
                        </span>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Fechado</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </section>
        )}

        {/* Avaliações */}
        {showReviewsSection && reviews && reviews.length > 0 && (
          <section>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold" style={{ color: themeDarkColor }}>Avaliações</h2>
              {totalReviews > 0 && (
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 fill-current" style={{ color: themeColor }} />
                  <span className="font-bold text-lg" style={{ color: themeDarkColor }}>{avgRating.toFixed(1)}</span>
                  <span className="text-sm" style={{ color: `${themeDarkColor}88` }}>({totalReviews})</span>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {reviews.slice(0, 6).map((review: any) => (
                <Card key={review.id} style={{ backgroundColor: "#fff", borderColor: `${themeColor}22` }}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                          style={{ backgroundColor: `${themeColor}22`, color: themeColor }}>
                          {(review.clientName ?? "C")[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="text-sm font-semibold leading-tight" style={{ color: themeDarkColor }}>{review.clientName ?? "Cliente"}</p>
                          <p className="text-xs" style={{ color: `${themeDarkColor}66` }}>
                            {new Date(review.createdAt).toLocaleDateString("pt-BR", { month: "short", year: "numeric" })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            className={`w-3.5 h-3.5 ${star <= review.rating ? "fill-current" : "opacity-20"}`}
                            style={{ color: themeColor }}
                          />
                        ))}
                      </div>
                    </div>
                    {review.comment && (
                      <p className="text-sm line-clamp-3" style={{ color: `${themeDarkColor}88` }}>{review.comment}</p>
                    )}
                    {review.adminReply && (
                      <div className="mt-3 pl-3 border-l-2 rounded-r-md py-2 pr-2"
                        style={{ borderColor: themeColor, backgroundColor: `${themeColor}11` }}>
                        <p className="text-xs font-semibold mb-0.5" style={{ color: themeColor }}>Resposta do salão</p>
                        <p className="text-xs line-clamp-3" style={{ color: `${themeDarkColor}88` }}>{review.adminReply}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>
        )}

        <Separator style={{ backgroundColor: `${themeColor}33` }} />

        {/* CTA final */}
        <section className="text-center py-8">
          <h2 className="text-2xl font-bold mb-3" style={{ color: themeDarkColor }}>Pronto para agendar?</h2>
          <p className="mb-6" style={{ color: `${themeDarkColor}88` }}>Escolha o serviço, o profissional e o horário que preferir.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button
              size="lg"
              className="text-white px-10"
              style={{ backgroundColor: themeColor }}
              onClick={() => navigate(`/booking?salon=${salon.id}`)}
            >
              <Calendar className="w-5 h-5 mr-2" />
              {ctaButtonText}
            </Button>
            {whatsappUrl && (
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">
                <Button size="lg" variant="outline" className="w-full sm:w-auto px-8"
                  style={{ borderColor: `${themeColor}66`, color: themeColor }}>
                  <MessageCircle className="w-5 h-5 mr-2" />
                  WhatsApp
                </Button>
              </a>
            )}
          </div>
        </section>
      </div>

      {/* Footer */}
      <footer className="border-t py-8 text-center text-sm" style={{ borderColor: `${themeColor}33`, color: `${themeDarkColor}66` }}>
        {/* Redes sociais no footer */}
        {(instagramUrl || facebookUrl || tiktokUrl || whatsappUrl) && (
          <div className="flex gap-4 justify-center mb-4">
            {instagramUrl && (
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity" style={{ color: themeColor }}>
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {facebookUrl && (
              <a href={facebookUrl} target="_blank" rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity" style={{ color: themeColor }}>
                <Facebook className="w-5 h-5" />
              </a>
            )}
            {whatsappUrl && (
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer"
                className="hover:opacity-70 transition-opacity" style={{ color: themeColor }}>
                <MessageCircle className="w-5 h-5" />
              </a>
            )}
          </div>
        )}
        <p>
          Agendamento online por{" "}
          <a href="/" className="hover:underline font-medium" style={{ color: themeColor }}>Beleza &amp; Bronze</a>
        </p>
      </footer>
    </div>
  );
}
